<script setup lang="ts">
import { computed, defineProps, defineEmits } from "vue";
import { BestDaysCount } from "@/types/calculators";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  startDay: string;
  endDay: string;
  bestDaysCount: BestDaysCount;
  dayStem: string;
  yearPillar: string;
  isCalendarView: boolean;
}>();
defineEmits<{
  (e: "showAsCalendar", asCalendar: boolean): void;
}>();

const { t } = useI18n();

const periodString = computed(() => {
  // 1. Переворачиваем даты в DD.MM.YYYY
  const start = props.startDay.split("-").reverse().join(".");
  const end = props.endDay.split("-").reverse().join(".");

  // 2. Считаем чистую разницу в днях
  const days =
    (Date.parse(props.endDay) - Date.parse(props.startDay)) / 86400000 + 1;

  // 3. Собираем строку, отдавая склонение на откуп i18n
  return `${start} — ${end} (${t("labels.days_period", days)})`;
});
</script>

<template>
  <div
    class="bg-white rounded-2xl border border-divider p-4 sm:p-6 shadow-sm space-y-5"
  >
    <div
      class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-divider"
    >
      <div>
        <div class="flex flex-wrap items-center gap-2 mb-1">
          <span
            class="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-beige text-active"
          >
            {{ t("calculators.personal-date.best-days-card.badge") }}
          </span>
          <span class="text-xs text-muted">{{ periodString }}</span>
        </div>
        <h2 class="text-xl sm:text-2xl font-bold text-brown">
          {{ t("calculators.personal-date.best-days-card.title") }}
          <span class="text-sm sm:text-base font-normal text-muted font-caudex">
            {{
              t("calculators.personal-date.best-days-card.diapason-params", {
                yearPillar: yearPillar,
                dayStem: dayStem,
              })
            }}
          </span>
        </h2>
        <p class="text-xs text-alternative-gray mt-1">
          {{ t("calculators.personal-date.best-days-card.desc") }}
        </p>
      </div>
      <div class="flex items-center gap-2 self-start lg:self-center">
        <span class="text-xs text-muted hidden sm:inline">
          {{ t("calculators.personal-date.best-days-card.view-toggle") }}
        </span>
        <div
          class="flex bg-secondary p-1 rounded-xl border border-divider text-xs"
        >
          <button
            class="font-bold shadow-sm px-3 py-1.5 rounded-lg transition-colors"
            :class="
              isCalendarView
                ? 'bg-white text-brown'
                : 'text-muted hover:text-brown'
            "
            @click="$emit('showAsCalendar', true)"
          >
            {{ t("calculators.personal-date.best-days-card.calendar") }}
          </button>
          <button
            class="px-3 py-1.5 rounded-lg transition-colors"
            :class="
              isCalendarView
                ? 'text-muted hover:text-brown'
                : 'bg-white text-brown'
            "
            @click="$emit('showAsCalendar', false)"
          >
            {{ t("calculators.personal-date.best-days-card.table") }}
          </button>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
      <div
        class="p-3 sm:p-4 rounded-xl border border-gold/40 bg-[#FFFDF9] hover:shadow-md transition-all cursor-pointer flex items-center gap-2.5 sm:gap-3.5"
      >
        <div
          class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gold/15 text-gold flex items-center justify-center font-bold text-base sm:text-xl shrink-0"
        >
          ★{{ bestDaysCount.golden_days }}
        </div>
        <div class="min-w-0">
          <div
            class="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#9A6E1E] truncate"
          >
            {{ t("calculators.personal-date.best-days-card.golden-days") }}
          </div>
          <div class="text-xs sm:text-sm font-bold text-brown truncate">
            {{ t("calculators.personal-date.best-days-card.perfect-dates") }}
          </div>
          <div class="text-[10px] text-muted hidden sm:block">
            {{ t("calculators.personal-date.best-days-card.tong-shu") }}
          </div>
        </div>
      </div>
      <div
        class="p-3 sm:p-4 rounded-xl border border-success/30 bg-[#F6FAF7] hover:shadow-md transition-all cursor-pointer flex items-center gap-2.5 sm:gap-3.5"
      >
        <div
          class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-success/15 text-success flex items-center justify-center font-bold text-base sm:text-xl shrink-0"
        >
          ✓{{ bestDaysCount.favorable_days }}
        </div>
        <div class="min-w-0">
          <div
            class="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-success truncate"
          >
            {{ t("calculators.personal-date.best-days-card.favourable") }}
          </div>
          <div class="text-xs sm:text-sm font-bold text-brown truncate">
            {{ t("calculators.personal-date.best-days-card.great-for-tasks") }}
          </div>
          <div class="text-[10px] text-muted hidden sm:block">
            {{ t("calculators.personal-date.best-days-card.target-starts") }}
          </div>
        </div>
      </div>
      <div
        class="p-3 sm:p-4 rounded-xl border border-divider bg-secondary/70 hover:shadow-md transition-all cursor-pointer flex items-center gap-2.5 sm:gap-3.5"
      >
        <div
          class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white text-brown border border-divider flex items-center justify-center font-bold text-base sm:text-xl shrink-0"
        >
          {{ bestDaysCount.personal_clash_days }}
        </div>
        <div class="min-w-0">
          <div
            class="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-alternative-gray truncate"
          >
            {{ t("calculators.personal-date.best-days-card.for-routine") }}
          </div>
          <div class="text-xs sm:text-sm font-bold text-brown truncate">
            {{ t("calculators.personal-date.best-days-card.current-tasks") }}
          </div>
          <div class="text-[10px] text-muted hidden sm:block">
            {{
              t(
                "calculators.personal-date.best-days-card.with-no-important-starts"
              )
            }}
          </div>
        </div>
      </div>
      <div
        class="p-3 sm:p-4 rounded-xl border border-error/30 bg-[#FDF7F7] hover:shadow-md transition-all cursor-pointer flex items-center gap-2.5 sm:gap-3.5"
      >
        <div
          class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-error/15 text-error flex items-center justify-center font-bold text-base sm:text-xl shrink-0"
        >
          ✕{{ bestDaysCount.general_taboo_days }}
        </div>
        <div class="min-w-0">
          <div
            class="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-error truncate"
          >
            {{ t("calculators.personal-date.best-days-card.breakout") }}
          </div>
          <div class="text-xs sm:text-sm font-bold text-brown truncate">
            {{ t("calculators.personal-date.best-days-card.card-conflict") }}
          </div>
          <div class="text-[10px] text-muted hidden sm:block">
            {{ t("calculators.personal-date.best-days-card.avoid-starting") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
